/**
 * Headless board → SVG. Walks a placed BoardSpace (the layout solver's
 * output) and assembles one portable SVG: each node is composed to a scene
 * BoxNode (by `nodeFor`, default `composeBoardNode`), laid out into its rect,
 * and translated to its board position. Nested spaces (subspaces) are
 * projected into their parent's body. Edges honor their `widget.*` style and
 * carry per-colour arrowhead markers (explicit fills — the Safari-safe
 * pattern the live board uses; `context-stroke` renders invisible there).
 *
 * No DOM, no browser — the whole model→graph→layout→render path runs in Node.
 */
import { layout, type Rect } from "../layout.js";
import { metricsMeasure } from "../metrics.js";
import { toSVG } from "../svg.js";
import type { Measure } from "../text.js";
import type { BoxNode } from "../model.js";
import { composeBoardNode } from "./compose-node.js";
import { findSubspaceSlot } from "./slot.js";
import { planRoutes } from "./edgePlanner.js";
import { FOCUS_GROUND_ALPHA, FOCUS_VEIL, groundStrokeOpacity } from "./focus-style.js";
import type { BoardItem, BoardSpace } from "./types.js";

export interface RenderBoardOptions {
  /** Compose a node's visual; return null to skip it. Default: the shared
   *  module-card composer (`composeBoardNode`). */
  readonly nodeFor?: (item: BoardItem) => BoxNode | null;
  readonly measure?: Measure;
  readonly background?: string;
  /**
   * Colour of the `focus` ground veil. Defaults to `background` when given,
   * else the export canvas's own near-black — never a scene token: `tokens.bg`
   * is navy where this canvas is neutral, so a token-sourced veil would tint
   * every receded node.
   */
  readonly veilColor?: string;
  /** Edge stroke colour when the edge has no widget style (default slate). */
  readonly edgeColor?: string;
  /** Padding (px) around the space frame on every side. */
  readonly padding?: number;
}

const DEFAULT_EDGE = "#475569";

/** The export canvas's own colour (mirrors `exportShared.BG`) — the veil's
 *  default, so an export with no explicit background still occludes against
 *  what it is actually drawn on. */
const DEFAULT_VEIL = "#131314";

const round = (n: number): number => Math.round(n * 100) / 100;

function stripSvg(svg: string): string {
  return svg.replace(/^<svg[^>]*>/, "").replace(/<\/svg>$/, "");
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Id-safe key for a stroke colour ("#f43f5e" → "f43f5e"). */
function colorKey(color: string): string {
  return color.replace(/[^a-zA-Z0-9]+/g, "");
}

/** Mirrors the live board's ArrowheadMarker (SpaceRenderer): explicit fill,
 *  userSpaceOnUse so the head keeps its size under group scaling. */
function arrowMarker(color: string): string {
  return (
    `<marker id="arrow-${colorKey(color)}" viewBox="0 0 10 10" refX="9" refY="5" ` +
    `markerWidth="8" markerHeight="8" orient="auto" markerUnits="userSpaceOnUse">` +
    `<path d="M 0 0 L 10 5 L 0 10 z" fill="${color}"/>` +
    `</marker>`
  );
}

interface RenderCtx {
  readonly measure: Measure;
  readonly nodeFor: (item: BoardItem) => BoxNode | null;
  readonly edgeColor: string;
  /** Colour of the `focus` ground veil. */
  readonly veil: string;
  /** Distinct arrowhead colours used anywhere in the (nested) render. */
  readonly markerColors: Set<string>;
  /** Monotonic node counter → a unique def-id prefix per node, so clipPaths /
   *  gradients from different nodes concatenated into one SVG never collide. */
  seq: number;
}

function renderSpace(sp: BoardSpace, out: string[], ctx: RenderCtx): void {
  // Item/route coords are origin-relative (solver space); the frame is
  // 0..width/height. Neutralize the origin — the same translate(-origin)
  // the live board applies — so content lands inside the frame.
  const ox = sp.originX || 0;
  const oy = sp.originY || 0;
  if (ox || oy) out.push(`<g transform="translate(${-ox},${-oy})">`);
  // Group frames first — background rects UNDER edges and nodes. Inline
  // scene tokens only (export resolves no stylesheet); the label sits in
  // the strip the layout reserved (FRAME_LABEL_H above the member bbox).
  // A frame is figure iff it holds ANY figure member (containment): its members
  // carry their own veils, so this only fades the frame's own backdrop and
  // label, which would otherwise stay bright around receded nodes.
  // Only STAMPED members count. A `Map<id, item.focus>` answers `has` for an
  // item whose focus is `undefined`, so probing membership rather than the
  // value made every frame on every unfocused `group by` export read as
  // all-ground and fade to 45%.
  const roleById = new Map(
    sp.items.flatMap((i) => (i.focus ? ([[i.id, i.focus]] as const) : [])),
  );
  for (const f of sp.groupFrames ?? []) {
    // Depth-stepped fill: nesting reads at a glance (array order = paint
    // order — placeGroupFrames emits depth-ascending, parents first).
    const fillOpacity = 0.12 + Math.min(3, f.depth ?? 0) * 0.06;
    // One group opacity rather than a per-attribute fade, so the rect and its
    // label recede together — the same uniform factor the veil applies.
    const stamped = f.memberIds.filter((id) => roleById.has(id));
    const ground = stamped.length > 0 && stamped.every((id) => roleById.get(id) !== "figure");
    out.push(
      `<g data-frame-id="${escapeXml(f.id)}"${ground ? ` opacity="${round(FOCUS_GROUND_ALPHA)}"` : ""}>` +
        `<rect x="${round(f.x)}" y="${round(f.y)}" width="${round(f.w)}" height="${round(f.h)}" rx="14" ` +
        `fill="#334155" fill-opacity="${fillOpacity}" stroke="#64748b" stroke-opacity="0.55" stroke-dasharray="7 5"/>` +
        `<text x="${round(f.x + 12)}" y="${round(f.y + 17)}" ` +
        `font-family="system-ui, -apple-system, &quot;Segoe UI&quot;, sans-serif" ` +
        `font-size="12" font-weight="600" fill="#94a3b8">${escapeXml(f.label)}</text>` +
        `</g>`,
    );
  }
  // Edges next (under the nodes). Routes are in this space's local coords —
  // the same frame the per-item translates are relative to.
  if (sp.edges?.length) {
    const items = new Map(sp.items.map((i) => [i.id, i]));
    // Live-conformance `violation` edges are a LIVE overlay (peer of live health
    // coloring) — excluded from static export so an exported image is model
    // truth, never a drift snapshot.
    const exportEdges = sp.edges.filter((e) => !e.violation);
    const routes = planRoutes(exportEdges, items);
    for (const e of exportEdges) {
      const r = routes.get(e.id);
      if (!r) continue;
      const ws = e.widgetStyle;
      const stroke = ws?.color ?? ctx.edgeColor;
      ctx.markerColors.add(stroke);
      const attrs = [
        `fill="none"`,
        `stroke="${stroke}"`,
        `stroke-width="${ws?.width ?? 1.5}"`,
      ];
      if (ws?.dashed) attrs.push(`stroke-dasharray="6 4"`);
      // A ground edge fades by the same FACTOR the veil takes off a ground node
      // — a 1.5px line always has the canvas behind it, so occluding it and
      // scaling its alpha are the same pixels. Applied here rather than folded
      // into `widgetStyle` so `applyFocus` stays idempotent.
      const opacity = e.focus === "ground" ? groundStrokeOpacity(ws?.opacity) : ws?.opacity;
      if (opacity != null && opacity < 1) attrs.push(`stroke-opacity="${round(opacity)}"`);
      attrs.push(`marker-end="url(#arrow-${colorKey(stroke)})"`);
      // Endpoint ids on the path let SVG consumers highlight a node's
      // edges (layout-loop untangling) without geometric matching.
      attrs.push(`data-from="${e.from}" data-to="${e.to}"`);
      out.push(`<path d="${r.d}" ${attrs.join(" ")}/>`);
    }
  }
  for (const item of sp.items) {
    const node = ctx.nodeFor(item);
    let slotRect: Rect | undefined;
    // data-mid names the module on its group so SVG consumers (layout-loop
    // drag, tooling, tests) can address nodes without positional matching.
    out.push(`<g data-mid="${item.id}" transform="translate(${item.x},${item.y})">`);
    if (node) {
      const lb = layout(node, { x: 0, y: 0, w: item.w, h: item.h }, ctx.measure);
      // The composed node's `[part="subspace"]` slot rect is where the host
      // projects the nested space — the same rect the live board measures.
      slotRect = findSubspaceSlot(lb);
      out.push(stripSvg(toSVG(lb, { idPrefix: `n${ctx.seq++}_` })));
    }
    // Project a nested subspace into the node's slot rect (precise, matches the
    // live board), or the body region (~lower 70%) when the node exposes no
    // slot — a widget-agnostic fallback.
    if (item.space && item.space.items.length) {
      const pad = item.w * 0.06;
      const region = slotRect
        ? { x: slotRect.x, y: slotRect.y, w: slotRect.w, h: slotRect.h }
        : { x: pad, y: item.h * 0.28, w: item.w - 2 * pad, h: item.h * 0.72 - pad };
      const sw = item.space.width || 1;
      const sh = item.space.height || 1;
      const k = Math.min(region.w / sw, region.h / sh, 1);
      out.push(`<g transform="translate(${round(region.x)},${round(region.y)}) scale(${round(k)})">`);
      renderSpace(item.space, out, ctx);
      out.push(`</g>`);
    }
    // The ground veil, LAST in the item's group — above the composed node AND
    // above the nested space projected on top of it, so a receded container
    // takes its children with it. Painted in the canvas's own colour, so the
    // node is behind glass rather than repainted: every channel underneath
    // (hue, lifecycle opacity, dash, strike, badge) survives and still traces
    // to its legend row.
    if (item.focus === "ground") {
      out.push(
        `<rect x="0" y="0" width="${round(item.w)}" height="${round(item.h)}" rx="12" ` +
          `fill="${ctx.veil}" fill-opacity="${FOCUS_VEIL}"/>`,
      );
    }
    out.push(`</g>`);
  }
  if (ox || oy) out.push(`</g>`);
}

export function renderBoardSvg(space: BoardSpace, opts: RenderBoardOptions = {}): string {
  const ctx: RenderCtx = {
    measure: opts.measure ?? metricsMeasure,
    nodeFor: opts.nodeFor ?? composeBoardNode,
    edgeColor: opts.edgeColor ?? DEFAULT_EDGE,
    veil: opts.veilColor ?? opts.background ?? DEFAULT_VEIL,
    markerColors: new Set(),
    seq: 0,
  };
  const pad = opts.padding ?? 0;
  const body: string[] = [];
  renderSpace(space, body, ctx);

  const w = Math.max(1, Math.round(space.width + 2 * pad));
  const h = Math.max(1, Math.round(space.height + 2 * pad));
  const bg = opts.background
    ? `<rect x="0" y="0" width="${w}" height="${h}" fill="${opts.background}"/>`
    : "";
  const defs = ctx.markerColors.size
    ? `<defs>${[...ctx.markerColors].map(arrowMarker).join("")}</defs>`
    : "";
  const content = pad
    ? `<g transform="translate(${pad},${pad})">${body.join("")}</g>`
    : body.join("");
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">` +
    defs +
    bg +
    content +
    `</svg>`
  );
}
